import { getItemsFromCollection } from "directus/src/operations";
import { useFetch } from "hooks/src/useFetch";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "ui/components/card";
import { RadioGroup, RadioGroupItem } from "ui/components/radio-group";
import { Skeleton } from "ui/components/skeleton";
import { Switch } from "ui/components/switch";
import { Label } from "ui/components/label";
import { cn } from "ui/lib/utils";
import {
    areQuestionSelectionsEqual,
    deriveValue,
    groupByCategory,
    normalizeQuestionIds,
} from "./survey-category-questions.utils";

const SurveyCategoryQuestionsSelector = ({ value, onChange, disabled }) => {
    const { data, isLoading, isError } = useFetch({
        queryKey: ["survey_questions"],
        queryFn: () =>
            getItemsFromCollection("survey_questions", {
                fields: ["id", "question", "category.id", "category.name", "category.status"],
                deep:{
                    category:{
                        _filter:{
                            status:{
                                _eq: "published"
                            }
                        },
                        _sort: ["name"],
                    }
                },
                filter: {
                    status: {
                        _eq: "published"
                    }
                },
                limit: -1,
            }),
    });

    const groupedCategories = useMemo(() => groupByCategory(data ?? []), [data]);

    const [activeCategories, setActiveCategories] = useState(new Set());
    const [selectedQuestions, setSelectedQuestions] = useState({});
    const skipNextEmitFromPropSyncRef = useRef(false);
    const valueRef = useRef(value);
    valueRef.current = value;

    useEffect(() => {
        if (Object.keys(groupedCategories).length === 0) return;

        if (!Array.isArray(value) || value.length === 0) {
            const currentDerived = deriveValue(activeCategories, selectedQuestions);
            if (
                activeCategories.size === 0 &&
                Object.keys(selectedQuestions).length === 0 &&
                normalizeQuestionIds(currentDerived).length === 0
            ) {
                return;
            }
            skipNextEmitFromPropSyncRef.current = true;
            setActiveCategories(new Set());
            setSelectedQuestions({});
            return;
        }

        const questionToCategory = {};
        for (const [categoryId, { questions }] of Object.entries(groupedCategories)) {
            for (const q of questions) {
                questionToCategory[q.id] = categoryId;
            }
        }

        const newActiveCategories = new Set();
        const newSelectedQuestions = {};

        for (const questionId of value) {
            const categoryId = questionToCategory[questionId];
            if (categoryId != null) {
                newActiveCategories.add(categoryId);
                newSelectedQuestions[categoryId] = questionId;
            }
        }

        const currentDerived = deriveValue(activeCategories, selectedQuestions);
        const proposedDerived = deriveValue(newActiveCategories, newSelectedQuestions);
        if (
            areQuestionSelectionsEqual(proposedDerived, value) &&
            areQuestionSelectionsEqual(currentDerived, value)
        ) {
            return;
        }

        skipNextEmitFromPropSyncRef.current = true;
        setActiveCategories(newActiveCategories);
        setSelectedQuestions(newSelectedQuestions);
    }, [value, groupedCategories]);

    useEffect(() => {
        if (skipNextEmitFromPropSyncRef.current) {
            skipNextEmitFromPropSyncRef.current = false;
            return;
        }
        if (typeof onChange !== "function") return;
        const next = deriveValue(activeCategories, selectedQuestions);
        if (areQuestionSelectionsEqual(next, valueRef.current)) return;
        onChange(next);
    }, [activeCategories, selectedQuestions, onChange]);

    const handleToggleCategory = (categoryId, checked) => {
        if (checked) {
            setActiveCategories((prev) => new Set([...prev, categoryId]));
        } else {
            setActiveCategories((prev) => {
                const next = new Set(prev);
                next.delete(categoryId);
                return next;
            });
            setSelectedQuestions((prev) => {
                if (!(categoryId in prev)) return prev;
                const next = { ...prev };
                delete next[categoryId];
                return next;
            });
        }
    };

    const handleSelectQuestion = (categoryId, questionId) => {
        setSelectedQuestions((prev) => ({ ...prev, [categoryId]: questionId }));
    };

    if (isLoading && !data) {
        return (
            <div className="flex flex-col gap-3">
                {[1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className="rounded-lg border border-border bg-card px-4 py-3.5 shadow-none"
                    >
                        <Skeleton className="h-5 w-2/5" />
                    </div>
                ))}
            </div>
        );
    }

    // Error state
    if (isError) {
        return <p className="text-sm text-destructive">Failed to load survey categories</p>;
    }

    // Empty state
    if (Object.keys(groupedCategories).length === 0) {
        return <p className="text-sm text-muted-foreground">No categories available</p>;
    }

    return (
        <div className="flex flex-col gap-3">
            {Object.entries(groupedCategories).map(([categoryId, { name, questions }]) => {
                const isOpen = activeCategories.has(categoryId);
                return (
                    <Card
                        key={categoryId}
                        className={cn(
                            "gap-0 overflow-hidden rounded-lg border border-border bg-card py-0 shadow-none",
                            "transition-colors"
                        )}
                    >
                        <div className="flex items-center justify-between gap-4 p-4">
                            <Label
                                htmlFor={`category-switch-${categoryId}`}
                                className="cursor-pointer text-sm font-medium leading-tight text-foreground"
                            >
                                {name}
                            </Label>
                            <Switch
                                checked={isOpen}    
                                onCheckedChange={(checked) => handleToggleCategory(categoryId, checked)}
                                disabled={disabled}
                                id={`category-switch-${categoryId}`}
                            />
                        </div>
                        {isOpen && questions.length > 0 && (
                            <div className="border-t border-border/70 px-4 pb-4 pt-3">
                                <RadioGroup
                                    value={selectedQuestions[categoryId] ?? ""}
                                    onValueChange={(qId) => handleSelectQuestion(categoryId, qId)}
                                    disabled={disabled}
                                    className="flex flex-col gap-3.5"
                                >
                                    {questions.map((question) => (
                                        <div
                                            key={question.id}
                                            className="flex items-start gap-3"
                                        >
                                            <RadioGroupItem
                                                value={question.id}
                                                id={`question-${question.id}`}
                                                disabled={disabled}
                                                className="mt-0.5 shrink-0"
                                            />
                                            <Label
                                                htmlFor={`question-${question.id}`}
                                                className="cursor-pointer text-sm font-normal leading-snug text-muted-foreground"
                                            >
                                                {question.question}
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>
                        )}
                        {isOpen && questions.length === 0 && (
                            <div className="border-t border-border/70 px-4 py-3 text-sm text-muted-foreground">
                                No questions in this category.
                            </div>
                        )}
                    </Card>
                );
            })}
        </div>
    );
};

const FormSurveyCategoryQuestions = ({
    label,
    required,
    error,
    value,
    onChange,
    disabled,
}) => {
    return (
        <div className="flex flex-col gap-3">
            {label && (
                <Label className="text-sm font-medium leading-none">
                    {label}
                    {required && <span className="ml-0.5 text-red-500">*</span>}
                </Label>
            )}
            <div
                className={cn(
                    "rounded-lg",
                    error && "ring-2 ring-destructive ring-offset-2 ring-offset-background"
                )}
            >
                <SurveyCategoryQuestionsSelector
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            </div>
            {error && (
                <p className="text-sm text-destructive">{error.message || error}</p>
            )}
        </div>
    );
};

export { SurveyCategoryQuestionsSelector, FormSurveyCategoryQuestions };
